import { ReactNode } from 'react';
import { AssessmentSessionProcessTypeId, Status } from '../../services/exams/assessmentSession/assessments.types';
import { IPageInfo } from '../../models/page.types';
import { AssessmentQuality } from '../../models/assessment.types';
import { SelectableTablePossibleCellsType } from '../../components/SelectableTable/SelectableTable.types';

export type AssessmentSortBy =
  | 'examTaskGroupSolutionScoreId'
  | 'pointsSum'
  | 'progress'
  | 'downloadDate'
  | 'updateDate'
  | 'examinerName';

export enum AssessmentSortById {
  ExamTaskGroupSolutionScoreId = 1,
  Points,
  Progress,
  DownloadDate,
  UploadDate,
  ExaminerName,
}

export interface RawTaskGroupSolutionProgress {
  newTaskGroupSolutions: number;
  openTaskGroupSolutions: number;
  assessedTaskGroupSolutions: number;
  closedTaskGroupSolutions: number;
}

export interface RawAssessmentSessionProgress {
  deskLimit?: number | null;
  totalLimit?: number | null;
}

export interface ExamTaskGroupSolutionsProgressData {
  deskLimit?: number | null;
  totalLimit?: number | null;
  newTaskGroupSolutions: number;
  openTaskGroupSolutions: number;
  assessedTaskGroupSolutions: number;
  closedTaskGroupSolutions: number;
}

export enum FlagDefinitionType {
  Yellow = 1,
  Orange,
  Red,
  Pink,
  Violet,
  Blue,
  NavyBlue,
  Green,
  Brown,
  Grey,
}

export interface ExamTaskGroupSolutionScoreFlag {
  flagId: string;
  flagDefinitionId: FlagDefinitionType;
  color: string;
  name: string;
  isActive: boolean;
  examinerId?: string;
  examinerFunction?: ExaminerFunction;
  comment?: string;
  isVerifier?: boolean;
}

export interface ChangedScoreInfo {
  previousScore: number;
  supervisorRole: string;
}

export enum VerificationState {
  assigned,
  noChanges,
  pendingApproval,
  scoreSelected,
}

export enum FinalScoreAuthor {
  Examiner = 0,
  Verifier = 1,
}

export interface ExamTaskGroupSolutions {
  tasksSolutionSheetId: string;
  examTaskGroupSolutionId: string;
  assessmentSessionProcessId: string;
  assessmentSessionProcessTypeId: AssessmentSessionProcessTypeId;
  status: Status;
  examinerId: string;
  firstName: string;
  lastName: string;
  pointsSum: number;
  sampleAssessmentPoints: number | null;
  finalAssessmentQuality: AssessmentQuality | null;
  progress: number;
  //alerts?: Notifications; <--Sspecial case should be returned inside of an object that contains also messages and other notification in the future
  isSpecialCase: boolean;
  withMessage: boolean;
  downloadDate: Date;
  updateDate?: Date;
  examTaskGroupSolutionScoreId: string;
  isSendbackResultFailedExceptionalCase?: boolean;
  examTaskGroupSolutionScoreFlags: ExamTaskGroupSolutionScoreFlag[];
  isScoringAdjusted: boolean;
  isControlProcessType: boolean;
  isCommentOnCriteria: boolean;
  isCommentOnImage: boolean;
  isCommentOffPage: boolean;
  examTaskNumbersWithNoAnswer: string[];
  changedScoreInfo: ChangedScoreInfo | null;
  hasClosedSpecialCase: boolean;
  verificationState: VerificationState;
}

export interface ExamTaskGroupSolutionsNavigation {
  firstId: string | null;
  prevId: string | null;
  nextId: string | null;
  lastId: string | null;
  isLoading?: boolean;
}

export interface ExamTaskGroupSolutionsTableItem {
  [key: string]: SelectableTablePossibleCellsType;
  id: string;
  flag: JSX.Element;
  examTaskGroupSolutionScoreId: JSX.Element;
  tableEnterURL: string;
  examinerName: JSX.Element;
  status: JSX.Element;
  pointsSum: ReactNode;
  sampleAssessmentPoints: number | null;
  progress: JSX.Element;
  alerts?: JSX.Element;
  updateDate: JSX.Element;
  downloadDate: JSX.Element;
  actions: JSX.Element;
}

export interface Notifications {
  isCommentOnImage: boolean;
  isCommentOffPage: boolean;
  isCommentOnCriteria: boolean;
  warnings?: boolean;
  emails?: boolean;
  isScoringAdjusted?: boolean;
  isControlProcessType?: boolean;
  changedScoreInfo?: ChangedScoreInfo | null;
  closedSpecialCase?: boolean;
}

export interface ExamTaskGroupSolutionPerExaminersListItem {
  examTaskGroupSolutionId: string;
  assessmentSessionProcessId: string;
  examTaskGroupId: string;
  examinerId: string;
  status: Status;
  pointsSum: string;
  progress: number;
  sampleAssessmentPoints: number | null;
  updateDate: Date;
  downloadDate: Date;
}

export interface ExamTaskGroupSolutionPerExaminers {
  pageInfo: IPageInfo;
  list: ExamTaskGroupSolutionPerExaminersListItem[];
}

export enum DownloadType {
  single = 0,
  fillToLimits = 1,
}

export enum ExaminerFunction {
  KK = 0,
  KGZ = 1,
  PZE = 2,
  CZE = 3,
  PZWO = 4,
  CZWO = 5,
}

export enum ExaminerSampleAssessmentGroupFunction {
  PZWO = 0,
  CZWO = 1,
}

export const examinerFunctionAdapter = (
  examinerSampleAssessmentGroupFunction: ExaminerSampleAssessmentGroupFunction | null,
): ExaminerFunction | undefined => {
  switch (examinerSampleAssessmentGroupFunction) {
    case ExaminerSampleAssessmentGroupFunction.CZWO:
      return ExaminerFunction.CZWO;
    case ExaminerSampleAssessmentGroupFunction.PZWO:
      return ExaminerFunction.PZWO;
    default:
      return undefined;
  }
};

export const getTooltipColorName = (flag: ExamTaskGroupSolutionScoreFlag) => {
  switch (flag.flagDefinitionId) {
    case FlagDefinitionType.Yellow:
      return 'Żółty';
    case FlagDefinitionType.Orange:
      return 'Pomarańczowy';
    case FlagDefinitionType.Red:
      return 'Czerwony';
    case FlagDefinitionType.Pink:
      return 'Różowy';
    case FlagDefinitionType.Violet:
      return 'Fioletowy';
    case FlagDefinitionType.Blue:
      return 'Niebieski';
    case FlagDefinitionType.NavyBlue:
      return 'Granatowy';
    case FlagDefinitionType.Green:
      return 'Zielony';
    case FlagDefinitionType.Brown:
      return 'Brązowy';
    case FlagDefinitionType.Grey:
      return 'Szary';

    default:
      return '-';
  }
};

export const getExaminerFunctionLabel = (examinerFunction: ExaminerFunction, isVerifier?: boolean) => {
  switch (examinerFunction) {
    case ExaminerFunction.KK:
      return 'KK';
    case ExaminerFunction.KGZ:
      return 'KGZ';
    case ExaminerFunction.PZE:
      return 'PZE';
    case ExaminerFunction.CZE:
      return isVerifier ? 'W' : 'E';
    case ExaminerFunction.CZWO:
      return 'CZWO';
    case ExaminerFunction.PZWO:
      return 'PZWO';
    default:
      return '';
  }
};

interface IObjectKeys {
  [key: string]: string | string[];
}

export interface Filters extends IObjectKeys {
  flag: string | string[];
  examTaskGroupSolutionScoreId: string | string[];
  examinerName: string | string[];
  status: string | string[];
  pointsFrom: string | string[];
  pointsTo: string | string[];
  progress: string | string[];
  alerts: string | string[];
  verificationState: string | string[];
  downloadDateFrom: string | string[];
  downloadDateTo: string | string[];
  updateDateFrom: string | string[];
  updateDateTo: string | string[];
  assessmentQualityId: string | string[];
  examTaskNumbersWithNoAnswer: string;
}

export const getFunctionLabel = (fun: ExaminerFunction | null, isVerifier: boolean | null = false): string => {
  switch (fun) {
    case ExaminerFunction.KK:
      return 'KK';
    case ExaminerFunction.KGZ:
      return 'KGZ';
    case ExaminerFunction.PZE:
      return 'PZE';
    case ExaminerFunction.CZE:
      return isVerifier ? 'W' : 'E';
    case ExaminerFunction.PZWO:
      return 'PZWO';
    case ExaminerFunction.CZWO:
      return 'CZWO';

    default:
      return '';
  }
};

export const getFunctionLabelWithVerifier = (fun: ExaminerFunction | null, isVerifier: boolean | undefined): string => {
  if (isVerifier) return 'W';

  switch (fun) {
    case ExaminerFunction.KK:
      return 'KK';
    case ExaminerFunction.KGZ:
      return 'KGZ';
    case ExaminerFunction.PZE:
      return 'PZE';
    case ExaminerFunction.CZE:
      return 'E';
    case ExaminerFunction.PZWO:
      return 'PZWO';
    case ExaminerFunction.CZWO:
      return 'CZWO';

    default:
      return '';
  }
};

export enum AssignVerifierError {
  LimitError = 'The number of ORGZ ids must be between 1 and 500.',
  NoVerifier = 'Verifier was not found.',
  NoMatchingExamTaskGroupSolutionScores = 'No matching ExamTaskGroupSolutionScores were found for the provided ids.',
  ControlSolutionScore = 'At least one selected exam task group solution score is control.',
  OpenOrNew = 'At least one selected exam task group solution score has not been assessed yet.',
  UpdatedByKKOrKGZ = 'At least one selected exam task group solution score was updated by KK or KGZ.',
  InVerification = 'At least one selected exam task group solution score is already under verification.',
}

export const getAssignVerifierErrorLabel = (error: AssignVerifierError | null): string => {
  switch (error) {
    case AssignVerifierError.LimitError:
      return 'Możesz przypisać jednorazowo 500 prac.';
    case AssignVerifierError.NoVerifier:
      return 'Nie możesz przypisać prac. W zespole nie zdefiniowano funkcji weryfikatora.';
    case AssignVerifierError.ControlSolutionScore:
      return 'zostały wskazane prace kontrolne';
    case AssignVerifierError.OpenOrNew:
      return 'ocenianie przez egzaminatora nie zostało ukończone';
    case AssignVerifierError.UpdatedByKKOrKGZ:
      return 'zostały wskazane prace zmodyfikowane przez KK/KGZ';
    case AssignVerifierError.InVerification:
      return 'zostały wskazane prace, które zostały już wcześniej przypisane do weryfikacji';

    default:
      return '';
  }
};
